import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft, Package, ChevronDown, ChevronUp, MapPin, Phone, User as UserIcon,
    Edit3, Check, Wallet, CreditCard, ShoppingBag,
} from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SavedAddressesSection from "../components/SavedAddressesSection";
import { toast } from "sonner";

const statusLabel = (status, t) => {
    return t(`order_status_${status}`) || status;
};

const statusColor = (status) => {
    switch (status) {
        case "placed": return "bg-blue-100 text-blue-700";
        case "confirmed": return "bg-amber-100 text-amber-700";
        case "out_for_delivery": return "bg-purple-100 text-purple-700";
        case "delivered": return "bg-emerald-100 text-emerald-700";
        case "cancelled": return "bg-rose-100 text-rose-700";
        default: return "bg-neutral-100 text-neutral-600";
    }
};

const OrderRow = ({ order }) => {
    const { t, formatPrice } = useI18n();
    const [expanded, setExpanded] = useState(false);
    const short = order.id.slice(0, 8).toUpperCase();
    const date = new Date(order.created_at).toLocaleString();
    const items = order.items || [];

    return (
        <div
            data-testid={`order-row-${order.id}`}
            className="bg-white border border-neutral-200 rounded-2xl overflow-hidden"
        >
            <button
                onClick={() => setExpanded((v) => !v)}
                data-testid={`order-expand-${order.id}`}
                className="w-full p-4 flex items-center gap-3 text-left hover:bg-neutral-50/60 transition-colors"
            >
                <div className="h-10 w-10 rounded-xl bg-[#FFF3EE] text-[#FF5722] flex items-center justify-center shrink-0">
                    <Package className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold text-sm">#{short}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusColor(order.status)}`}>
                            {statusLabel(order.status, t)}
                        </span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5 truncate">
                        {date} · {items.length} {t("filter_all").toLowerCase()}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className="font-display text-lg font-semibold">
                        {formatPrice(order.subtotal_all).primary}
                    </div>
                    <div className="text-[10px] text-neutral-400">
                        {formatPrice(order.subtotal_all).secondary}
                    </div>
                </div>
                {expanded
                    ? <ChevronUp className="h-4 w-4 text-neutral-400 ml-2" />
                    : <ChevronDown className="h-4 w-4 text-neutral-400 ml-2" />}
            </button>

            {expanded && (
                <div
                    data-testid={`order-detail-${order.id}`}
                    className="px-5 pb-5 pt-1 border-t border-neutral-100 bg-neutral-50/50"
                >
                    <div className="text-xs text-neutral-500 uppercase tracking-wider font-semibold mt-3 mb-2">
                        {t("account_items")}
                    </div>
                    <ul className="space-y-2">
                        {items.map((it, i) => (
                            <li key={`${order.id}-${i}`} className="flex items-center gap-3 text-sm">
                                {it.image && (
                                    <img src={it.image} alt="" className="h-10 w-10 rounded-lg object-cover bg-white" />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="truncate font-medium">{it.name}</div>
                                    {it.weight && <div className="text-[11px] text-neutral-500">{it.weight}</div>}
                                </div>
                                <div className="text-neutral-600 text-xs">x{it.qty}</div>
                                <div className="font-semibold text-sm whitespace-nowrap">
                                    {formatPrice(it.price_all * it.qty).primary}
                                </div>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-4 pt-3 border-t border-neutral-200 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <div className="flex items-start gap-1.5">
                            <MapPin className="h-3.5 w-3.5 mt-0.5 text-neutral-400 shrink-0" />
                            <span className="text-neutral-700">{order.address}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            {order.payment_method === "card"
                                ? <CreditCard className="h-3.5 w-3.5 text-neutral-400" />
                                : <Wallet className="h-3.5 w-3.5 text-neutral-400" />}
                            <span className="text-neutral-700">
                                {order.payment_method === "card"
                                    ? t("payment_method_card_paid")
                                    : t("payment_method_cod")}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AccountPage = () => {
    const { t } = useI18n();
    const { user, isAuthed, logout, setUser } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState(null);
    const [editingName, setEditingName] = useState(false);
    const [name, setName] = useState(user?.name || "");
    const [savingName, setSavingName] = useState(false);

    useEffect(() => {
        if (!isAuthed) {
            navigate("/", { replace: true });
            return;
        }
        api.get("/orders/mine")
            .then((r) => setOrders(r.data.orders || []))
            .catch(() => setOrders([]));
    }, [isAuthed, navigate]);

    const saveName = async () => {
        setSavingName(true);
        try {
            const r = await api.patch("/user/me", { name });
            // Refresh in-memory user immediately so the new name is visible without reload
            setUser(r.data);
            toast.success(t("account_name_saved"));
            setEditingName(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not save");
        } finally {
            setSavingName(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAF8]" data-testid="account-page">
            <Header onLogin={() => {}} />
            <main className="max-w-3xl mx-auto px-5 md:px-8 py-10">
                <Link
                    to="/"
                    data-testid="account-back-link"
                    className="text-sm text-neutral-600 hover:text-[#FF5722] inline-flex items-center gap-1 mb-5"
                >
                    <ArrowLeft className="h-4 w-4" /> {t("account_back_to_shop")}
                </Link>

                <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">
                    {t("account_title")}
                </h1>

                {/* ─── Profile card ─── */}
                <section className="mt-7 bg-white border border-neutral-200 rounded-2xl p-6">
                    <h2 className="font-display text-xl font-medium tracking-tight flex items-center gap-2">
                        <UserIcon className="h-5 w-5 text-[#FF5722]" />
                        {t("account_profile")}
                    </h2>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">
                                {t("account_phone")}
                            </div>
                            <div className="mt-1 text-sm font-semibold inline-flex items-center gap-1.5">
                                <Phone className="h-3.5 w-3.5 text-neutral-400" />
                                {user?.phone}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">
                                {t("account_name")}
                            </div>
                            {editingName ? (
                                <div className="mt-1 flex items-center gap-2">
                                    <input
                                        data-testid="account-name-input"
                                        type="text"
                                        autoFocus
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="flex-1 h-9 px-3 rounded-lg bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm"
                                    />
                                    <button
                                        data-testid="account-name-save"
                                        onClick={saveName}
                                        disabled={savingName}
                                        className="h-9 px-3 rounded-lg bg-[#FF5722] text-white text-xs font-semibold disabled:opacity-60"
                                    >
                                        <Check className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : (
                                <button
                                    data-testid="account-name-edit"
                                    onClick={() => setEditingName(true)}
                                    className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold group"
                                >
                                    {user?.name || <span className="text-neutral-400 font-normal italic">{t("account_name_empty")}</span>}
                                    <Edit3 className="h-3.5 w-3.5 text-neutral-400 group-hover:text-[#FF5722] transition-colors" />
                                </button>
                            )}
                        </div>
                    </div>
                </section>

                {/* ─── Saved addresses ─── */}
                <SavedAddressesSection />

                {/* ─── Orders ─── */}
                <section className="mt-7">
                    <h2 className="font-display text-xl font-medium tracking-tight flex items-center gap-2 mb-4">
                        <ShoppingBag className="h-5 w-5 text-[#FF5722]" />
                        {t("account_orders")}
                        {orders && (
                            <span className="text-xs font-normal text-neutral-500 ml-1">
                                ({orders.length})
                            </span>
                        )}
                    </h2>
                    {orders === null ? (
                        <div className="space-y-3">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-20 bg-white border border-neutral-200 rounded-2xl animate-pulse" />
                            ))}
                        </div>
                    ) : orders.length === 0 ? (
                        <div
                            data-testid="account-empty-orders"
                            className="bg-white border border-neutral-200 rounded-2xl p-8 text-center text-neutral-500"
                        >
                            <Package className="h-10 w-10 mx-auto text-neutral-300" />
                            <p className="mt-3 text-sm">{t("account_no_orders")}</p>
                            <Link
                                to="/"
                                className="inline-flex items-center gap-1.5 mt-4 px-5 h-10 rounded-full bg-[#FF5722] hover:bg-[#E64A19] text-white text-sm font-semibold transition-colors"
                            >
                                {t("account_start_shopping")}
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="account-orders-list">
                            {orders.map((o) => <OrderRow key={o.id} order={o} />)}
                        </div>
                    )}
                </section>

                <button
                    onClick={() => { logout(); navigate("/"); }}
                    data-testid="account-logout-btn"
                    className="mt-10 text-sm text-rose-600 hover:text-rose-700 font-medium"
                >
                    {t("nav_logout")}
                </button>
            </main>
            <Footer />
        </div>
    );
};

export default AccountPage;
